/**
 * Replace part of a string with another string value
 *
 * @param {string} origin - original string
 * @param {number} startIndex - start index
 * @param {number} endIndex - end index
 * @param {string} insertion - string to be inserted
 * @returns {string} Final string value
 */
const replaceBetween = (origin, startIndex, endIndex, insertion) => {
    return (
        origin.substring(0, startIndex) + insertion + origin.substring(endIndex)
    );
};

/**
 * Interpolate Prismic text
 *
 * @param {string|object} text - Prismic text
 * @param {object} interpolations - interpolations map
 * @returns {string|object} Interpolated text
 */
export const interpolateText = (text, interpolations) => {
    if (!interpolations || !text || typeof text !== 'string') return text;

    for (const [key, value] of Object.entries(interpolations)) {
        // eslint-disable-next-line no-param-reassign
        text = text.replace(new RegExp(key, 'g'), value);
    }

    return text;
};

/**
 * Interpolate text in Prismic page object
 *
 * @param {object} nestedValue - Prismic page object
 * @param {object} interpolations - interpolations map
 * @returns {object} Interpolated Prismic page object
 */
export const interpolateNestedValue = (nestedValue, interpolations) => {
    let text = nestedValue?.text;

    // No text property, nothing to interpolate
    if (!text) return nestedValue;

    const hasAnyInterpolationMatches = Object.keys(interpolations)
        .map(key => new RegExp(key, 'g'))
        .flatMap(pattern => [...text.matchAll(pattern)]);

    // No matches for interpolation
    if (!hasAnyInterpolationMatches) {
        return nestedValue;
    }

    // No span objects to modify. Interpolate and return
    if (!nestedValue.spans.length) {
        return {
            ...nestedValue,
            text: interpolateText(text, interpolations),
        };
    }

    // Prismic stores text marked as bold, italic or link as span objects that have start and end properties.
    // When interpolating text the start and end properties of spans will become invalid.
    // We have to adjust these start and end properties after interpolation.
    // Algorithm:
    // 1. Replace text for each span (start to end) with a unique placeholder.
    // 2. Interpolate text.
    // 3. Find each unique placeholder and adjust start, end & restore original value.

    // counter is used for generating unique placeholders
    let counter = 0;

    // Transform and replace text for each span with a unique placeholder
    const placeholderSpans = nestedValue.spans.map(span => {
        const {start, end} = span;
        const str = text.substr(start, end - start);
        const placeholder = `${counter++}`.padStart(str.length, 'ø');

        text = replaceBetween(text, span.start, span.end, placeholder);

        return {
            ...span,
            // store placeholder and original value (str) in a namespaced property spothero
            // these are needed later to restore original value in text
            spothero: {
                placeholder,
                str,
            },
        };
    });

    // interpolate text
    text = interpolateText(text, interpolations);

    // restore original values and adjust start & end
    const spans = placeholderSpans.map(span => {
        const {str, placeholder} = span.spothero;
        const start = text.indexOf(placeholder);
        const end = start + placeholder.length;

        text = replaceBetween(text, start, end, str);

        delete span.spothero;

        return {
            ...span,
            start,
            end,
        };
    });

    return {
        ...nestedValue,
        text,
        spans,
    };
};

/**
 * Interpolate page meta data
 *
 * @param {object} page - Prismic page object
 * @param {object} interpolations - interpolations map
 * @returns {object} Interpolated page object
 */

export const interpolatePageMeta = (page, interpolations) => {
    const {
        meta_title_transient: metaTitleTransient,
        meta_description_transient: metaDescriptionTransient,
    } = page;

    return {
        ...page,
        // eslint-disable-next-line camelcase
        meta_title_transient: interpolateText(
            metaTitleTransient,
            interpolations
        ),
        // eslint-disable-next-line camelcase
        meta_description_transient: interpolateText(
            metaDescriptionTransient,
            interpolations
        ),
    };
};
